import React, { useEffect, useState } from 'react';
import { Loader2, ChevronRight, X, FileText, Copy, Mail, ShieldCheck, Download } from 'lucide-react';
import {
	getVendors,
	getVendorDetail,
	getReconStatus,
	setVendorReview,
	inr,
	inrFull,
	triggerBlobDownload,
} from '../data';
import type { VendorRow, VendorDetail, VendorPayment, AppliedBillDetail, ReconStatus } from '../types';

const BUCKETS = [
	{ id: '', label: 'All' },
	{ id: 'paid_no_bill', label: 'Paid, no bill' },
	{ id: 'overdue_with_trail', label: 'Overdue + email' },
	{ id: 'overdue_no_trail', label: 'Overdue, no email' },
	{ id: 'ok', label: 'No exception' },
];

/**
 * Payment expand layout switch.
 * - 'v2' = card fence + accordion + Bank/Find/Auth proof tray (current default)
 * - 'v1' = previous flat expand (invoices + VerificationStrip + stacked details)
 * Flip to 'v1' to revert without deleting the old UI.
 */
// `as` keeps the union wide: with a plain annotation TS narrows the const to its literal
// and then flags the 'v1' branch as unreachable, even though flipping this back is the point.
const PAYMENT_EXPAND_LAYOUT = 'v2' as 'v1' | 'v2';

const ACCORDION_MS = 300;

/**
 * Smooth open/close for payment expand panels.
 * Uses grid 0fr→1fr (no max-height guess) and keeps children mounted briefly on close
 * so the collapse can finish before unmount.
 */
const SmoothCollapse: React.FC<{ open: boolean; children: React.ReactNode }> = ({
	open,
	children,
}) => {
	const [mounted, setMounted] = useState(false);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {
		if (open) {
			setMounted(true);
			// Two frames: paint 0fr with content, then expand to 1fr so the transition runs.
			const id = window.requestAnimationFrame(() => {
				window.requestAnimationFrame(() => setExpanded(true));
			});
			return () => window.cancelAnimationFrame(id);
		}
		setExpanded(false);
		const id = window.setTimeout(() => setMounted(false), ACCORDION_MS);
		return () => window.clearTimeout(id);
	}, [open]);

	return (
		<div
			className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
				expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
			}`}
			aria-hidden={!open}>
			<div
				className={`min-h-0 overflow-hidden transition-opacity duration-200 ease-out motion-reduce:transition-none ${
					expanded ? 'opacity-100' : 'opacity-0'
				}`}>
				{mounted ? children : null}
			</div>
		</div>
	);
};

// A leftover balance this small is rounding/TDS "dust" (₹0.20, ₹0.90) — not a real overdue.
const DUST_MAX = 10;
const isDust = (balance: number | string) => {
	const b = Number(balance);
	return b > 0 && b <= DUST_MAX;
};

// issue-tag pill styling
const FLAG_STYLE: Record<string, string> = {
	overdue: 'bg-red-100 text-red-700',
	outstanding: 'bg-amber-100 text-amber-700',
	no_email_trail: 'bg-gray-200 text-gray-700',
	paid_no_bill: 'bg-purple-100 text-purple-700',
	requests_ignored: 'bg-orange-100 text-orange-700',
	bank_unverified: 'bg-rose-100 text-rose-700',
};
const FLAG_LABEL: Record<string, string> = {
	overdue: 'Overdue',
	outstanding: 'Outstanding',
	no_email_trail: 'No email',
	paid_no_bill: 'Paid, no bill',
	requests_ignored: 'Requests ignored',
	bank_unverified: 'Zoho≠Bank',
};

// Filters survive a page refresh via localStorage — the user wants the filter
// state to "remember" across reloads, not just across tab switches.
const FILTERS_KEY = 'finrecon-vendor-filters';
interface StoredFilters {
	bucket: string;
	flag: string;
	search: string;
	period: string;
}
const loadStoredFilters = (): StoredFilters => {
	try {
		const raw = localStorage.getItem(FILTERS_KEY);
		if (!raw) return { bucket: '', flag: '', search: '', period: '' };
		return { bucket: '', flag: '', search: '', period: '', ...JSON.parse(raw) };
	} catch {
		return { bucket: '', flag: '', search: '', period: '' };
	}
};

// Period picker (month or fiscal year, Apr–Mar) replaces free-form date
// inputs. Data starts April 2025 — see ReconciliationDashboard's header
// comment — so options never reach further back than that.
const DATA_START = { year: 2025, month: 4 };
const MONTH_NAMES = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];
const pad2 = (n: number) => String(n).padStart(2, '0');
const lastDayOfMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

interface PeriodOption {
	value: string; // 'month:YYYY-MM' | 'fy:YYYY'
	label: string;
	from: string;
	to: string;
}

const buildFyOptions = (): PeriodOption[] => {
	const now = new Date();
	const currentFyStart = now.getMonth() + 1 >= DATA_START.month ? now.getFullYear() : now.getFullYear() - 1;
	const options: PeriodOption[] = [];
	for (let fy = currentFyStart; fy >= DATA_START.year; fy -= 1) {
		options.push({
			value: `fy:${fy}`,
			label: `FY ${fy}-${String(fy + 1).slice(-2)}`,
			from: `${fy}-04-01`,
			to: `${fy + 1}-03-31`,
		});
	}
	return options;
};

const buildMonthOptions = (): PeriodOption[] => {
	const now = new Date();
	const options: PeriodOption[] = [];
	let y = now.getFullYear();
	let m = now.getMonth() + 1;
	while (y > DATA_START.year || (y === DATA_START.year && m >= DATA_START.month)) {
		options.push({
			value: `month:${y}-${pad2(m)}`,
			label: `${MONTH_NAMES[m - 1]} ${y}`,
			from: `${y}-${pad2(m)}-01`,
			to: `${y}-${pad2(m)}-${pad2(lastDayOfMonth(y, m))}`,
		});
		m -= 1;
		if (m === 0) {
			m = 12;
			y -= 1;
		}
	}
	return options;
};

const FY_OPTIONS = buildFyOptions();
const MONTH_PERIOD_OPTIONS = buildMonthOptions();
const PERIOD_MAP = new Map<string, PeriodOption>(
	[...FY_OPTIONS, ...MONTH_PERIOD_OPTIONS].map(o => [o.value, o])
);

/**
 * What the payment dots mean. Rendered on the dashboard and under the invoice table from the
 * same component — a legend that disagrees with the thing it explains is worse than none.
 */
const DOT_LEGEND: Array<{ cls: string; label: string; title: string }> = [
	{ cls: 'bg-emerald-500', label: 'verified', title: 'Bank debit found and the bank line names the vendor' },
	{ cls: 'bg-amber-500', label: 'match or statement problem', title: 'Matched an internal transfer, the statement for that account is missing, or the only proof is an OTP' },
	{ cls: 'bg-rose-500', label: 'paid a different party', title: 'The bank line names some other party — the one worth chasing' },
	{ cls: 'bg-slate-300', label: 'unprovable', title: 'The bank line carries no payee name, so it can be neither confirmed nor denied' },
];

const DotLegend: React.FC<{ className?: string }> = ({ className = '' }) => (
	<span className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 ${className}`}>
		{DOT_LEGEND.map(d => (
			<span key={d.label} className='inline-flex items-center gap-1' title={d.title}>
				<span className={`h-1.5 w-1.5 rounded-full ${d.cls}`} />
				{d.label}
			</span>
		))}
	</span>
);

/** Vendor reconciliation — the core view. Bucket filter → table → drill-down. */
export const VendorRecon: React.FC = () => {
	const [rows, setRows] = useState<VendorRow[]>([]);
	const [total, setTotal] = useState(0);
	const [initialFilters] = useState(loadStoredFilters);
	const [bucket, setBucket] = useState(initialFilters.bucket);
	const [flag, setFlag] = useState(initialFilters.flag);
	const [search, setSearch] = useState(initialFilters.search);
	const [period, setPeriod] = useState(initialFilters.period);
	const selectedPeriod = PERIOD_MAP.get(period);
	const from = selectedPeriod?.from ?? '';
	const to = selectedPeriod?.to ?? '';
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);
	const [detail, setDetail] = useState<VendorDetail | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [status, setStatus] = useState<ReconStatus | null>(null);
	const limit = 25;

	useEffect(() => {
		getReconStatus().then(setStatus).catch(() => undefined);
	}, []);

	useEffect(() => {
		localStorage.setItem(FILTERS_KEY, JSON.stringify({ bucket, flag, search, period }));
	}, [bucket, flag, search, period]);

	useEffect(() => {
		let on = true;
		setLoading(true);
		getVendors({
			bucket,
			flag,
			search,
			from: from || undefined,
			to: to || undefined,
			sortBy: 'outstanding',
			order: 'desc',
			limit,
			offset,
		})
			.then(r => {
				if (!on) return;
				setRows(r.data);
				setTotal(r.pagination.total);
			})
			.finally(() => on && setLoading(false));
		return () => {
			on = false;
		};
	}, [bucket, flag, search, from, to, offset]);

	const openDetail = (norm: string) => {
		setDetailLoading(true);
		setDetail(null);
		// Same period as the list — a founder scoped to FY 2025-26 shouldn't land
		// in a drawer that quietly shows all-time numbers instead.
		getVendorDetail(norm, { from: from || undefined, to: to || undefined })
			.then(setDetail)
			.finally(() => setDetailLoading(false));
	};

	return (
		<div className='space-y-4'>
			{status && (
				<section className='bg-white rounded-lg border border-gray-200 p-4'>
					<div className='grid grid-cols-3 gap-6'>
						<div>
							<div className='text-xs text-gray-500 uppercase tracking-wide'>
								Payments bank-verified
							</div>
							<div className='text-2xl font-bold text-gray-900 mt-1'>
								{status.bank_match.verified_pct}%
							</div>
							<div className='text-xs text-gray-400 mt-0.5'>
								{status.bank_match.verified} of {status.bank_match.payments.toLocaleString()}{' '}
								payments
							</div>
						</div>
						<div>
							<div className='text-xs text-gray-500 uppercase tracking-wide'>Unverified value</div>
							<div className='text-2xl font-bold text-rose-600 mt-1'>
								{inr(status.bank_match.unverified_value)}
							</div>
							<div className='text-xs text-gray-400 mt-0.5'>awaiting bank confirmation</div>
						</div>
						<div>
							<div className='text-xs text-gray-500 uppercase tracking-wide'>Clean vendors</div>
							<div className='text-2xl font-bold text-green-600 mt-1'>
								{status.totals.clean_vendors} / {status.totals.vendors}
							</div>
							<div className='text-xs text-gray-400 mt-0.5'>no open exceptions</div>
						</div>
					</div>

					<div className='border-t border-gray-100 mt-4 pt-4'>
						<div className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2'>
							Needs attention
						</div>
						<div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
							{status.issues.map(i => {
								const active = flag === i.flag;
								return (
									<button
										key={i.flag}
										onClick={() => {
											setFlag(active ? '' : i.flag);
											setBucket('');
											setOffset(0);
										}}
										className={`text-left rounded-md -m-1 p-1 transition ${
											active ? 'ring-2 ring-blue-200 bg-blue-50' : 'hover:bg-gray-50'
										}`}>
										<div className='text-xs text-gray-500'>{i.label}</div>
										<div className='text-sm font-semibold text-amber-600 mt-0.5'>
											{i.vendors}
											{i.value != null ? ` · ${inr(i.value)}` : ''}
										</div>
									</button>
								);
							})}
						</div>
					</div>
				</section>
			)}

			<div className='flex flex-wrap items-center gap-2'>
				{BUCKETS.map(b => (
					<button
						key={b.id}
						onClick={() => {
							setBucket(b.id);
							setFlag('');
							setOffset(0);
						}}
						className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
							bucket === b.id && !flag
								? 'bg-gray-900 text-white'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}>
						{b.label}
					</button>
				))}
				{/* Zoho≠Bank: vendors with a payment not matched to a bank line. Respects the
				    date range — set dates + click to get exactly the unreconciled vendors. */}
				<button
					onClick={() => {
						setFlag(flag === 'bank_unverified' ? '' : 'bank_unverified');
						setBucket('');
						setOffset(0);
					}}
					title='Payments in Zoho with no matching bank debit (date-scoped)'
					className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${
						flag === 'bank_unverified'
							? 'bg-rose-600 text-white'
							: 'bg-rose-50 text-rose-700 hover:bg-rose-100'
					}`}>
					<span
						className={`w-1.5 h-1.5 rounded-full ${
							flag === 'bank_unverified' ? 'bg-white' : 'bg-rose-500'
						}`}
					/>
					Zoho≠Bank
				</button>
				<div className='ml-auto flex items-center gap-2'>
					<select
						value={period}
						onChange={e => {
							setPeriod(e.target.value);
							setOffset(0);
						}}
						className='px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white'
						title='Period — scopes money figures to a month or fiscal year'>
						<option value=''>All time</option>
						<optgroup label='Fiscal year'>
							{FY_OPTIONS.map(o => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</optgroup>
						<optgroup label='Month'>
							{MONTH_PERIOD_OPTIONS.map(o => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</optgroup>
					</select>
					<input
						value={search}
						onChange={e => {
							setSearch(e.target.value);
							setOffset(0);
						}}
						placeholder='Search vendor…'
						className='px-3 py-1.5 rounded-lg border border-gray-200 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-200'
					/>
				</div>
			</div>
			{(flag || period) && (
				<div className='text-xs text-gray-500'>
					Scope —{' '}
					{selectedPeriod && (
						<>
							money figures reflect <b>{selectedPeriod.label}</b> ({selectedPeriod.from} →{' '}
							{selectedPeriod.to});{' '}
						</>
					)}
					{flag && (
						<>
							filtered to <b>{FLAG_LABEL[flag] || flag}</b>;{' '}
						</>
					)}
					email and exception counts reflect full vendor history.
				</div>
			)}

			{/* the same legend the drawer uses — open a vendor and the dots mean this */}
			<div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
				<span className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400'>
					Payment status
				</span>
				<DotLegend />
			</div>

			<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-gray-500 text-xs uppercase'>
						<tr>
							<th className='text-left px-4 py-3'>Vendor</th>
							<th className='text-right px-4 py-3'>Bills</th>
							<th className='text-right px-4 py-3'>Outstanding</th>
							<th className='text-right px-4 py-3'>Overdue</th>
							<th className='text-right px-4 py-3'>Paid</th>
							<th className='text-right px-4 py-3'>Emails</th>
							<th className='text-right px-4 py-3'>Req · Ign</th>
							<th className='px-4 py-3'></th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={8} className='px-4 py-8 text-center text-gray-400'>
									<Loader2 className='animate-spin inline' size={18} /> Loading…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={8} className='px-4 py-8 text-center text-gray-400'>
									No vendors match.
								</td>
							</tr>
						) : (
							rows.map(v => (
								<tr
									key={v.id}
									onClick={() => openDetail(v.vendor_norm)}
									className='border-t border-gray-100 hover:bg-blue-50/40 cursor-pointer'>
									<td className='px-4 py-3'>
										<div className='flex items-center gap-2'>
											{/* Severity at a glance: red = bank mismatch (most concrete
											    discrepancy — Zoho and the bank disagree), amber = some
											    other open flag, green = nothing outstanding. */}
											<span
												className={`w-2 h-2 rounded-full shrink-0 ${
													v.flags?.includes('bank_unverified')
														? 'bg-red-500'
														: v.flags && v.flags.length > 0
														? 'bg-amber-500'
														: 'bg-green-500'
												}`}
											/>
											<div className='font-medium text-gray-900'>{v.vendor_raw}</div>
										</div>
										<div className='flex flex-wrap gap-1 mt-1 pl-4'>
											{v.review_status && (
												<span
													className={`px-1.5 py-0.5 rounded text-[11px] ${
														v.review_status === 'reviewed_ok'
															? 'bg-green-100 text-green-700'
															: 'bg-blue-100 text-blue-700'
													}`}>
													{v.review_status === 'reviewed_ok' ? 'reviewed ✓' : 'needs review'}
												</span>
											)}
											{v.flags && v.flags.length > 0 ? (
												v.flags.map(f => (
													<span
														key={f}
														className={`px-1.5 py-0.5 rounded text-[11px] ${
															FLAG_STYLE[f] || 'bg-gray-100 text-gray-600'
														}`}>
														{FLAG_LABEL[f] || f}
													</span>
												))
											) : (
												<span className='px-1.5 py-0.5 rounded text-[11px] bg-green-100 text-green-700'>
													clean
												</span>
											)}
										</div>
									</td>
									<td className='px-4 py-3 text-right text-gray-600'>{v.bills}</td>
									<td className='px-4 py-3 text-right font-medium'>{inr(v.outstanding)}</td>
									<td className='px-4 py-3 text-right text-red-600'>{inr(v.overdue_value)}</td>
									<td className='px-4 py-3 text-right text-gray-600'>{inr(v.paid)}</td>
									<td className='px-4 py-3 text-right text-gray-600'>{v.email_threads}</td>
									<td className='px-4 py-3 text-right'>
										<span className='text-gray-600'>{v.payment_requests}</span>
										{v.requests_ignored > 0 && (
											<span className='text-red-600'> / {v.requests_ignored}</span>
										)}
									</td>
									<td className='px-4 py-3 text-gray-300'>
										<ChevronRight size={16} />
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className='flex items-center justify-between text-sm text-gray-500'>
				<span>
					{total.toLocaleString()} vendors · showing {offset + 1}–
					{Math.min(offset + limit, total)}
				</span>
				<div className='flex gap-2'>
					<button
						disabled={offset === 0}
						onClick={() => setOffset(Math.max(0, offset - limit))}
						className='px-3 py-1 rounded border border-gray-200 disabled:opacity-40'>
						Prev
					</button>
					<button
						disabled={offset + limit >= total}
						onClick={() => setOffset(offset + limit)}
						className='px-3 py-1 rounded border border-gray-200 disabled:opacity-40'>
						Next
					</button>
				</div>
			</div>

			{(detail || detailLoading) && (
				<VendorDrawer
					detail={detail}
					loading={detailLoading}
					onClose={() => {
						setDetail(null);
						setDetailLoading(false);
					}}
				/>
			)}
		</div>
	);
};


/**
 * Payment-anchored, not invoice-anchored.
 *
 * A payment is one bank debit — one thing a person can verify. An invoice is not:
 * it can be settled by several payments across months, so anchoring on invoices
 * draws one debit as many events and repeats the same email under each. Anchoring
 * on payments means every rupee appears once, in the month it left the account.
 *
 * Invoices that no payment claims (or that still carry a balance) can't be reached
 * from the payment list, so they get their own short section rather than padding
 * the timeline with settled ₹0 rows.
 */
const buildLedger = (detail: VendorDetail) => {
	// A bill is "claimed" if ANYTHING settled it, taken from the server's settlement block
	// rather than from the payments on screen. Two things get missed otherwise:
	//   - a credit note settled it, so no payment claims it
	//   - the payment that settled it falls outside the selected window
	// settlement.paid_in_cash counts every payment on that bill, in any period.
	const claimed = new Set<string>();
	detail.payments.forEach(p => (p.applied_bills ?? []).forEach(b => claimed.add(b.bill_number)));
	detail.bills.forEach(b => {
		const st = b.settlement;
		if (Number(st?.paid_in_cash ?? 0) > 0 || Number(st?.credit_total ?? 0) > 0) {
			claimed.add(b.bill_number);
		}
	});

	// Oldest first: a financial year should read April → March, the way the books do.
	//
	// Ties break on payment number, and that is not cosmetic: the running balance on each
	// invoice (open_before / left_after) is computed server-side in exactly this order, so
	// listing two same-day payments the other way round makes the balances read backwards.
	// Zoho stores only a date, no time, so payment number is the only ordering we have.
	const payments = [...detail.payments].sort(
		(a, b) =>
			String(a.payment_date).localeCompare(String(b.payment_date)) ||
			String(a.payment_number).localeCompare(String(b.payment_number), undefined, { numeric: true })
	);

	// Grouped by payment month — April first, the way the books run. The month band
	// carries the month subtotal; the row's own date column carries the day.
	const months: { key: string; label: string; payments: VendorPayment[]; total: number }[] = [];
	payments.forEach(p => {
		const key = String(p.payment_date).slice(0, 7);
		let bucket = months.find(m => m.key === key);
		if (!bucket) {
			const [y, m] = key.split('-');
			bucket = { key, label: `${MONTH_NAMES[Number(m) - 1]} ${y}`, payments: [], total: 0 };
			months.push(bucket);
		}
		bucket.payments.push(p);
		bucket.total += Number(p.amount);
	});

	const openBills = detail.bills.filter(
		b => Number(b.balance) > 0 || !claimed.has(b.bill_number)
	);

	// email_refs is computed server-side per payment (identifier lookup against
	// bill numbers / bank reference) — authoritative, so a thread is "matched"
	// once any payment claims it.
	const matchedThreadIds = new Set<string>();
	detail.payments.forEach(p => (p.email_refs ?? []).forEach(r => matchedThreadIds.add(r.thread_id)));
	const unmatchedThreads = detail.threads.filter(t => !matchedThreadIds.has(t.provider_thread_id));

	// Invoice-centric view for the Invoices tab: every bill, what it cost, how much of it
	// is settled, and which payments claim it. Derived from the same payments, so the two
	// tabs can never disagree.
	const invoices = detail.bills.map(b => {
		const paidBy = detail.payments.filter(p =>
			(p.applied_bills ?? []).some(ab => ab.bill_number === b.bill_number)
		);
		const total = Number(b.total);
		const balance = Number(b.balance);
		const st = b.settlement;
		return {
			bill: b,
			total,
			balance,
			settled: total - balance,
			// cash / credit notes / TDS come from the server's settlement breakdown, which is
			// the only place that knows a bill can be cleared without money moving
			cash: Number(st?.paid_in_cash ?? 0),
			spillOver: Number(st?.spill_over ?? 0),
			spillFrom: st?.spill_over_payments ?? [],
			creditTotal: Number(st?.credit_total ?? 0),
			credits: st?.credit_notes ?? [],
			tds: Number(st?.tds ?? 0),
			unexplained: Number(st?.unexplained ?? 0),
			fullySettled: balance <= 0,
			paidBy,
			unclaimed: paidBy.length === 0 && Number(st?.credit_total ?? 0) === 0,
		};
	});

	return { months, openBills, unmatchedThreads, invoices };
};

// Three states, three colours, carried on a rail down the left of each payment row —
// the state is readable before any text is.
const STATE_RAIL: Record<string, string> = {
	proven: 'bg-emerald-500',
	unverified_name: 'bg-rose-500',
	no_bank: 'bg-slate-300',
};
// rose is reserved for a real payee problem; coverage gaps are amber, unprovable is grey
const REASON_RAIL: Record<string, string> = {
	internal_transfer: 'bg-amber-500',
	statement_missing: 'bg-amber-500',
	different_payee: 'bg-rose-500',
	no_payee_named: 'bg-slate-300',
	no_bank_line: 'bg-slate-300',
	otp_only: 'bg-amber-500',
	no_match_row: 'bg-slate-300',
};
/** The single source of truth for a payment's colour, used by the rail, the status chip and
 *  the invoice-tab dots. Before this existed the Invoices tab still coloured by raw state, so
 *  a payment reading amber "Matched an internal transfer" showed red one tab over. */
const paymentDot = (p: VendorPayment) =>
	(p.reason_code && REASON_RAIL[p.reason_code]) || STATE_RAIL[p.state] || 'bg-slate-300';

/** Left-edge accent for the expanded payment card — mirrors paymentDot colours. */
const paymentAccentBorder = (p: VendorPayment) => {
	const rail = paymentDot(p);
	if (rail.includes('emerald')) return 'border-l-emerald-500';
	if (rail.includes('rose')) return 'border-l-rose-500';
	if (rail.includes('amber')) return 'border-l-amber-500';
	return 'border-l-slate-300';
};

const STATE_LABEL: Record<string, string> = {
	proven: 'Verified',
	unverified_name: 'Name mismatch',
	no_bank: 'No bank line',
};

// The status chip reads off reason_code where there is one. "Name mismatch" implies we paid
// the wrong company; for most of these the truth is that the matched line is one of our own
// internal transfers, picked because the real debit sits in a statement we do not hold.
/** Short labels for the status column (narrow). Full sentence stays in `p.reason`. */
const REASON_LABEL: Record<string, string> = {
	internal_transfer: 'Internal transfer',
	statement_missing: 'Statement missing',
	different_payee: 'Different payee',
	no_payee_named: 'Payee not named',
	no_bank_line: 'No bank line',
	otp_only: 'OTP only',
	no_match_row: 'Not checked',
};
// only `different_payee` is a genuine payee problem; the rest are coverage or narration gaps
const REASON_TONE: Record<string, string> = {
	internal_transfer: 'text-amber-700',
	statement_missing: 'text-amber-700',
	different_payee: 'text-rose-700',
	no_payee_named: 'text-slate-500',
	no_bank_line: 'text-slate-500',
	otp_only: 'text-amber-700',
	no_match_row: 'text-slate-500',
};
const STATE_TEXT: Record<string, string> = {
	proven: 'text-emerald-700',
	unverified_name: 'text-rose-700',
	no_bank: 'text-slate-500',
};

const shortDate = (d: string) => {
	const [y, m, day] = String(d).slice(0, 10).split('-');
	return `${day} ${MONTH_NAMES[Number(m) - 1]?.slice(0, 3)} ${String(y).slice(-2)}`;
};

/**
 * Every invoice, what it cost, how much is settled, and which payments settled it.
 *
 * The Payments tab answers "did this money leave and to whom". This answers the other
 * half — "is each invoice paid for" — which the payment view cannot show, because an
 * invoice nobody paid appears under no payment at all.
 */
const InvoiceTable: React.FC<{
	rows: Array<{
		bill: { zoho_bill_id: string; bill_number: string; date: string; status: string };
		total: number;
		balance: number;
		settled: number;
		cash: number;
		spillOver: number;
		spillFrom: Array<{ payment_number: string; date: string }>;
		creditTotal: number;
		credits: Array<{ credit_note: string; amount: number }>;
		tds: number;
		unexplained: number;
		fullySettled: boolean;
		unclaimed: boolean;
		paidBy: VendorPayment[];
	}>;
}> = ({ rows }) => {
	const billed = rows.reduce((a, r) => a + r.total, 0);
	const cash = rows.reduce((a, r) => a + r.cash, 0);
	const credits = rows.reduce((a, r) => a + r.creditTotal, 0);
	const tds = rows.reduce((a, r) => a + r.tds, 0);
	const open = rows.reduce((a, r) => a + r.balance, 0);
	const spill = rows.reduce((a, r) => a + r.spillOver, 0);
	return (
		<div>
			<div className='mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600'>
				<span>
					Billed <span className='font-mono tabular-nums text-slate-900'>{inrFull(billed)}</span>
				</span>
				<span>
					Paid in cash <span className='font-mono tabular-nums text-slate-900'>{inrFull(cash)}</span>
				</span>
				{credits > 0 && (
					<span>
						Credit notes{' '}
						<span className='font-mono tabular-nums text-emerald-700'>{inrFull(credits)}</span>
					</span>
				)}
				<span>
					Still open{' '}
					<span className={`font-mono tabular-nums ${open > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
						{inrFull(open)}
					</span>
				</span>
				{Math.abs(spill) > 1 && (
					<span
						className='text-slate-500'
						title='Part-settled by a payment made before this window — the money moved, the split was never pulled'>
						Spill-over <span className='font-mono tabular-nums'>{inrFull(spill)}</span>
					</span>
				)}
				{tds > 0 && (
					<span className='text-slate-500' title='Tax withheld — already netted off the invoice total'>
						TDS withheld <span className='font-mono tabular-nums'>{inrFull(tds)}</span>
					</span>
				)}
			</div>
			<div className='overflow-hidden rounded-lg border border-slate-200'>
				<div className='grid grid-cols-[1fr_84px_112px_112px_104px_96px_1.1fr] items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500'>
					<span>Invoice</span>
					<span>Dated</span>
					<span className='text-right'>Total</span>
					<span className='text-right'>Paid in cash</span>
					<span className='text-right' title='Settled by a vendor credit note, no money moved'>
						Credit note
					</span>
					<span className='text-right'>Still open</span>
					<span>Cleared by</span>
				</div>
				{rows.map(r => (
					<div
						key={r.bill.zoho_bill_id}
						className='grid grid-cols-[1fr_84px_112px_112px_104px_96px_1.1fr] items-center gap-3 border-b border-slate-100 px-3 py-2 text-xs last:border-0'>
						<span className='truncate text-slate-800'>{r.bill.bill_number}</span>
						<span className='font-mono tabular-nums text-slate-500'>{shortDate(r.bill.date)}</span>
						<span className='text-right font-mono tabular-nums text-slate-900'>{inrFull(r.total)}</span>
						<span className='text-right font-mono tabular-nums text-slate-600'>
							{r.cash > 0 ? inrFull(r.cash) : '—'}
						</span>
						<span className='text-right font-mono tabular-nums text-emerald-700'>
							{r.creditTotal > 0 ? inrFull(r.creditTotal) : '—'}
						</span>
						<span
							className={`text-right font-mono tabular-nums ${
								r.balance > 0 ? (isDust(r.balance) ? 'text-slate-400' : 'text-rose-700') : 'text-slate-300'
							}`}>
							{r.balance > 0 ? inrFull(r.balance) : '—'}
						</span>
						<span className='flex flex-wrap items-center gap-1'>
							{r.paidBy.map(p => (
								<span
									key={p.zoho_payment_id}
									title={`${shortDate(p.payment_date)} · ${inrFull(p.amount)}`}
									className='inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700'>
									<span className={`h-1.5 w-1.5 rounded-full ${paymentDot(p)}`} />
									#{p.payment_number}
								</span>
							))}
							{r.credits.map(cn => (
								<Tag key={cn.credit_note} tone='emerald'>
									{cn.credit_note}
								</Tag>
							))}
							{r.paidBy.length === 0 && r.creditTotal === 0 && (
								<Tag tone='amber'>nothing claims it</Tag>
							)}
							{Number(r.tds) > 0 && <Tag tone='slate'>TDS {inrFull(r.tds)}</Tag>}
							{Math.abs(r.spillOver) > 1 && (
								<Tag tone='slate'>
									{inrFull(Math.abs(r.spillOver))} spill-over from{' '}
									{r.spillFrom.map(x => `#${x.payment_number}`).join(', ')}
								</Tag>
							)}
							{Math.abs(r.unexplained) > 1 && (
								<Tag tone='rose'>{inrFull(Math.abs(r.unexplained))} unexplained</Tag>
							)}
							{r.balance > 0 && isDust(r.balance) && <Tag tone='slate'>dust</Tag>}
						</span>
					</div>
				))}
			</div>
			<div className='mt-1.5 text-[11px] text-slate-500'>
				<DotLegend className='mr-3' />
				An invoice clears three ways: cash, a vendor credit note, or it stays open. Billed = cash +
				credit notes + still open. Spill-over is the part settled by a payment made before this
				window opened &mdash; the money moved, only its per-invoice split was never pulled. TDS is already netted off the invoice total, so it is shown but
				never added in. A payment can settle several invoices, so a payment number can appear on
				more than one row.
			</div>
		</div>
	);
};


/**
 * CSV export of what is on screen.
 *
 * Built client-side from the drawer's own data so the file can never disagree with the
 * screen. The Payments sheet is one row per payment-INVOICE pair, not per payment: an
 * accounts person needs the applied amount beside the invoice it went to, and a payment
 * covering seven invoices is seven lines of work, not one.
 */
const csvCell = (v: unknown) => {
	const t = v === null || v === undefined ? '' : String(v);
	// leading = / + / - / @ would be read as a formula by Excel; prefix to neutralise
	const safe = /^[=+\-@]/.test(t) ? `'${t}` : t;
	return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/**
 * An identifier that would change if a spreadsheet read it as a number.
 *
 * 609 of our bill numbers start with a zero ("0075"), and Excel silently turns those into
 * 75 — the invoice number in the export then does not match the invoice number in Zoho.
 * Quoting does not help; Excel coerces quoted numerics too. The ="..." form is the only
 * thing it honours, so it is used ONLY where coercion would actually lose something,
 * rather than on every identifier.
 */
const csvIdent = (v: unknown) => {
	const t = v === null || v === undefined ? '' : String(v);
	if (!t) return '';
	const wouldChange = /^[0-9]+$/.test(t) && String(Number(t)) !== t;
	return wouldChange ? `="${t}"` : csvCell(t);
};

// columns holding an identifier rather than a value or a label
const IDENT_COLS = new Set([
	'invoice',
	'payment_number',
	'reference',
	'credit_note_numbers',
	'bank_account',
	'settled_by',
]);

const downloadCsv = (filename: string, rows: Array<Record<string, unknown>>) => {
	if (!rows.length) return;
	const cols = Object.keys(rows[0]);
	const body = [
		cols.join(','),
		...rows.map(r => cols.map(c => (IDENT_COLS.has(c) ? csvIdent(r[c]) : csvCell(r[c]))).join(',')),
	].join('\n');
	// BOM so Excel opens rupee symbols and Devanagari correctly
	const blob = new Blob(['\uFEFF' + body], { type: 'text/csv;charset=utf-8;' });
	triggerBlobDownload(blob, filename);
};

/**
 * UI-only CSV (no backend). Sorted by payment date so Excel reads like the screen.
 * Lead columns are the accounts working set; everything else trails.
 * One row per payment-invoice pair — a payment covering 7 invoices is 7 lines.
 */
const paymentRows = (detail: VendorDetail) => {
	const payments = [...detail.payments].sort(
		(a, b) =>
			String(a.payment_date).localeCompare(String(b.payment_date)) ||
			String(a.payment_number).localeCompare(String(b.payment_number), undefined, { numeric: true })
	);
	return payments.flatMap(p => {
		const rowFor = (b?: AppliedBillDetail) => {
			const left = b?.open_after;
			// Match the on-screen "settled" label: left after this payment is 0.
			// partial = this payment hit the invoice but balance remains (cleared by a later one).
			const settled =
				left === null || left === undefined
					? ''
					: Number(left) <= 0
					? 'yes'
					: 'partial';
			return {
			vendor: detail.vendor.vendor_raw,
			payment_date: p.payment_date,
			'zoho#': p.payment_number,
			invoice: b?.bill_number || '',
			invoice_date: b?.date || '',
			invoice_total: b?.total ?? '',
			open: b?.open_before ?? '',
			applied: b?.amount_applied ?? '',
			left: left ?? '',
			settled,
			payment_amount: p.amount,
			applied_total: p.amount_applied_total ?? '',
			paid_in_advance: p.unapplied_amount ?? '',
			paid_through: p.paid_through_account_name || '',
			reference: p.reference?.value || '',
			status: p.state,
			why: p.reason_code || '',
			why_detail: p.reason || '',
			bank_account: p.bank?.bank_account || '',
			bank_date: p.bank?.bank_date || '',
			bank_amount: p.bank?.bank_amount ?? '',
			bank_says: p.bank?.bank_paid_to || '',
			day_gap: p.bank?.day_gap ?? '',
			date_flag: p.bank?.date_flag || '',
			statement_file: p.bank?.source?.kind === 'statement' ? p.bank.source.file : '',
			otp_authorised: p.authorised_by_otp ? 'yes' : 'no',
			reference_kind: p.reference?.kind || '',
		};
		};
		if (!p.applied_bills?.length) return [rowFor()];
		return p.applied_bills.map(b => rowFor(b));
	});
};

const invoiceRows = (detail: VendorDetail) => {
	const bills = [...detail.bills].sort((a, b) =>
		String(a.date).localeCompare(String(b.date)) ||
		String(a.bill_number).localeCompare(String(b.bill_number))
	);
	return bills.map(b => {
		const st = b.settlement;
		const paidBy = detail.payments
			.filter(p => (p.applied_bills ?? []).some(ab => ab.bill_number === b.bill_number))
			.map(p => `#${p.payment_number}`)
			.join(' ');
		return {
			vendor: detail.vendor.vendor_raw,
			invoice: b.bill_number,
			invoice_date: b.date,
			due_date: b.due_date || '',
			status: b.status,
			invoice_total: b.total,
			paid_in_cash: st?.paid_in_cash ?? '',
			still_open: b.balance,
			settled_by: paidBy,
			credit_notes: st?.credit_total ?? '',
			credit_note_numbers: (st?.credit_notes ?? []).map(c => c.credit_note).join(' '),
			tds_withheld: st?.tds ?? '',
			spill_over: st?.spill_over ?? '',
			unexplained: st?.unexplained ?? '',
		};
	});
};

/** One labelled square, used only in the legend above the table. */
const LegendDot: React.FC<{ on?: boolean; label: string }> = ({ on, label }) => (
	<span className='inline-flex items-center gap-1'>
		<span
			className={`h-2.5 w-2.5 rounded-[2px] ${
				on ? 'bg-emerald-500' : 'bg-slate-200 ring-1 ring-inset ring-slate-300'
			}`}
		/>
		{label}
	</span>
);

/** Four evidence slots, filled or hollow — the whole proof story in one column. */
const ProofDots: React.FC<{ p: VendorPayment }> = ({ p }) => {
	const slots = [
		{ on: p.proof?.bank, label: 'Bank debit found and it names this vendor' },
		{ on: p.proof?.invoice, label: 'Linked to at least one invoice' },
		{ on: (p.email_refs?.length ?? 0) > 0 || (p.email_evidence?.length ?? 0) > 0, label: 'Email names this payment' },
		{ on: (p.proofs?.length ?? 0) > 0, label: 'Document attached' },
	];
	return (
		<span className='inline-flex items-center gap-[3px]' aria-label='Evidence'>
			{slots.map((s, i) => (
				<span
					key={i}
					title={`${s.label} — ${s.on ? 'yes' : 'no'}`}
					className={`h-2.5 w-2.5 rounded-[2px] ${
						s.on ? 'bg-emerald-500' : 'bg-slate-200 ring-1 ring-inset ring-slate-300'
					}`}
				/>
			))}
		</span>
	);
};

/**
 * The invoices a payment touched.
 *
 * Zoho gives the LIST of invoices a payment was applied to, but for most payments not
 * the per-invoice amounts. So a ₹1,23,394 payment can legitimately list a ₹6,96,090
 * invoice — it paid part of it, and another payment paid the rest. Showing the invoice
 * total in the same weight as the payment amount reads as "we paid this", which is
 * wrong. The total is therefore set quietly, the column says `Invoice total`, and when
 * the listed totals don't equal the payment we say so in a sentence instead of leaving
 * the reader to do the arithmetic and conclude the data is broken.
 */
const AppliedInvoices: React.FC<{
	p: VendorPayment;
	dateScoped: boolean;
	window: { from: string | null; to: string | null } | null;
}> = ({ p, dateScoped, window: win }) => {
	const bills = p.applied_bills ?? [];
	const known = bills.every(b => b.amount_applied !== null);
	const listedTotal = bills.reduce((a, b) => a + Number(b.total || 0), 0);
	const unapplied = Number(p.unapplied_amount ?? 0);
	const periodLabel = win?.from ? `before ${shortDate(win.from)}` : 'outside this period';

	const tdsTotal = bills.reduce((a, b) => a + Number(b.tds ?? 0), 0);
	// Table already carries billed / open / applied / left — footer only for exceptions.
	const showNotes = tdsTotal > 0 || unapplied > 1 || !known;

	return (
		<div>
			<div className='overflow-hidden rounded border border-slate-200 bg-white'>
				<div className='grid grid-cols-[1fr_76px_104px_116px_112px_112px_1fr] items-center gap-3 border-b border-slate-100 bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500'>
					<span>Invoice</span>
					<span>Dated</span>
					<span className='text-right'>Invoice total</span>
					<span className='text-right' title='Still owed on this invoice just before this payment'>
						Open before
					</span>
					<span className='text-right'>Applied here</span>
					<span className='text-right' title='Still owed on this invoice right after this payment'>
						Left after
					</span>
					<span />
				</div>
				{bills.map(b => (
					<div
						key={b.bill_number}
						className='grid grid-cols-[1fr_76px_104px_116px_112px_112px_1fr] items-center gap-3 border-b border-slate-100 px-3 py-1.5 text-xs last:border-0'>
						<span className='truncate text-slate-800'>{b.bill_number}</span>
						<span className='font-mono tabular-nums text-slate-600'>
							{b.date ? shortDate(b.date) : '—'}
						</span>
						{/* what the vendor billed originally — context only */}
						<span className='text-right font-mono tabular-nums text-slate-500'>
							{b.total === null ? '—' : inrFull(b.total)}
						</span>
						{/* what was actually outstanding when this payment ran — the real starting point */}
						<span className='text-right font-mono tabular-nums text-slate-700'>
							{b.open_before === null ? '—' : inrFull(b.open_before)}
						</span>
						{/* what THIS payment put against it — the primary number */}
						<span className='text-right font-mono font-semibold tabular-nums text-slate-900'>
							{b.amount_applied === null ? '—' : inrFull(b.amount_applied)}
						</span>
						{/* what was still owed straight after — 0 means this payment finished it */}
						<span
							className={`text-right font-mono tabular-nums ${
								b.open_after === null
									? 'text-slate-400'
									: b.open_after > 0
									? 'text-amber-700'
									: 'text-emerald-700'
							}`}>
							{b.open_after === null ? '—' : b.open_after > 0 ? inrFull(b.open_after) : 'settled'}
						</span>
						<span className='flex flex-wrap gap-1.5'>
							{(b.credits ?? []).map(cn => (
								<Tag key={cn.credit_note} tone='emerald'>
									{cn.credit_note} {inrFull(cn.amount)}
								</Tag>
							))}
							{Number(b.balance) > 0 && !isDust(Number(b.balance)) && (
								<Tag tone='rose'>{inrFull(Number(b.balance))} still open</Tag>
							)}
							{isDust(Number(b.balance)) && <Tag tone='slate'>dust</Tag>}
							{b.paid_before_billed && <Tag tone='amber'>invoice dated after payment</Tag>}
							{!b.in_period && dateScoped && <Tag tone='slate'>dated {periodLabel}</Tag>}
							{b.status === 'not_found_in_zoho' && <Tag tone='rose'>not in Zoho</Tag>}
						</span>
					</div>
				))}
			</div>
			{showNotes && (
				<div className='mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-700'>
					{tdsTotal > 0 && (
						<span
							title='Tax we withheld and paid to the government instead of the vendor. Zoho records the invoice at the net figure, so the vendor’s own copy will read higher by this much.'>
							TDS withheld{' '}
							<span className='font-mono tabular-nums'>{inrFull(tdsTotal)}</span>
						</span>
					)}
					{unapplied > 1 && (
						<Tag tone='amber'>{inrFull(unapplied)} paid in advance, not yet on an invoice</Tag>
					)}
					{!known && (
						<span className='text-amber-800'>
							Some amounts are not yet pulled from Zoho — showing invoice totals for those (
							{inrFull(listedTotal)} listed).
						</span>
					)}
				</div>
			)}
		</div>
	);
};

/**
 * v1 backup — quiet bank/email proof footnote (pre–proof-tray layout).
 * Kept so PAYMENT_EXPAND_LAYOUT = 'v1' restores the previous expand UI.
 */
const VerificationStripV1: React.FC<{ p: VendorPayment }> = ({ p }) => {
	const src = p.bank?.source;
	const [copied, setCopied] = useState(false);
	if (!src || src.kind === 'none') {
		return (
			<div className='border-t border-dashed border-slate-200 pt-2 text-[11px] text-slate-400'>
				{src?.note || 'No bank line to check against.'}
			</div>
		);
	}
	const isEmail = src.kind === 'email';
	const token = isEmail ? src.subject : src.ref;
	const narration = isEmail ? src.subject : src.narration;
	return (
		<div className='border-t border-slate-100 pt-2'>
			<div className='flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px] text-slate-500'>
				<span className='shrink-0 font-medium text-slate-400'>
					{isEmail ? 'Email proof' : 'Bank proof'}
				</span>
				<span className='font-mono tabular-nums text-slate-400'>
					{src.account} · {shortDate(src.date || p.payment_date)}
				</span>
				{narration && (
					<span className='min-w-0 font-mono text-slate-600 break-all' title={String(narration)}>
						{narration}
					</span>
				)}
				{token && (
					<button
						onClick={() => {
							navigator.clipboard?.writeText(String(token));
							setCopied(true);
							setTimeout(() => setCopied(false), 1200);
						}}
						className='inline-flex items-center gap-1 font-mono tabular-nums text-slate-600 hover:text-slate-900'
						title={isEmail ? 'Copy subject' : 'Copy reference'}>
						{isEmail ? 'Copy subject' : String(token)}
						<Copy size={10} className='text-slate-400' />
					</button>
				)}
				{copied && <span className='text-emerald-600'>Copied</span>}
				{src.search_hint && <span className='text-slate-400'>{src.search_hint}</span>}
			</div>
			{isEmail && src.note && (
				<div className='mt-1 text-[11px] text-amber-700'>{src.note}</div>
			)}
		</div>
	);
};

/** Email list used inside Auth tray (v2) and Authorisation details (v1). */
const EmailEvidenceList: React.FC<{ p: VendorPayment }> = ({ p }) => (
	<div className='space-y-1.5'>
		{(p.email_evidence ?? []).map(e => (
			<div
				key={e.message_id}
				className={`rounded border px-3 py-2 text-xs ${
					e.proves === 'authorised_before_payment'
						? 'border-emerald-200 bg-emerald-50/60'
						: 'border-slate-200 bg-white'
				}`}>
				<div className='flex items-start gap-2'>
					{e.kind === 'otp' ? (
						<ShieldCheck
							size={13}
							className={`mt-0.5 shrink-0 ${
								e.proves === 'authorised_before_payment' ? 'text-emerald-600' : 'text-amber-600'
							}`}
						/>
					) : (
						<Mail size={12} className='mt-0.5 shrink-0 text-slate-500' />
					)}
					<div className='min-w-0 flex-1'>
						<div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
							<span className='font-medium text-slate-800'>{e.subject}</span>
							<span className='font-mono text-[11px] tabular-nums text-slate-600'>
								{shortDate(e.date)}
							</span>
							<span className='text-[11px] text-slate-600'>seen in {e.inboxes.join(', ')}</span>
							{e.proves === 'authorised_before_payment' && (
								<Tag tone='emerald'>authorised before payment</Tag>
							)}
							{e.proves === 'otp_after_payment' && (
								<Tag tone='amber'>OTP dated after the payment</Tag>
							)}
						</div>
						{e.kind !== 'otp' && e.excerpt && (
							<div className='mt-0.5 line-clamp-2 text-slate-600'>{e.excerpt}</div>
						)}
					</div>
				</div>
			</div>
		))}
	</div>
);

/**
 * v2 audit tray — three labeled roles after the invoice table:
 * Bank (narration + copy) · Find (file/search) · Auth (OTP status, expand for emails).
 */
const PaymentProofTrayV2: React.FC<{ p: VendorPayment }> = ({ p }) => {
	const src = p.bank?.source;
	const [copied, setCopied] = useState(false);
	const needsAttention = p.state !== 'proven';
	const isEmail = src?.kind === 'email';
	const token = src && src.kind !== 'none' ? (isEmail ? src.subject : src.ref) : null;
	const narration =
		src && src.kind !== 'none' ? (isEmail ? src.subject : src.narration) : null;
	const hasEmails = (p.email_evidence?.length ?? 0) > 0;

	return (
		<div
			className={`rounded-md border px-3 py-2.5 ${
				needsAttention ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-white'
			}`}>
			<div className='mb-2 text-xs font-semibold text-slate-700'>Proof</div>
			<div className='grid gap-2 sm:grid-cols-3'>
				{/* Bank */}
				<div className='min-w-0 rounded border border-slate-200 bg-slate-50/80 px-2.5 py-2'>
					<div className='text-[11px] font-semibold text-slate-700'>Bank</div>
					{!src || src.kind === 'none' ? (
						<p className='mt-1 text-[12px] text-slate-600'>
							{src?.note || 'No bank line to check against.'}
						</p>
					) : (
						<>
							<p className='mt-1 font-mono text-[11px] tabular-nums text-slate-600'>
								{src.account} · {shortDate(src.date || p.payment_date)}
							</p>
							{narration && (
								<p
									className='mt-1 line-clamp-2 font-mono text-[12px] leading-snug text-slate-800 break-all'
									title={String(narration)}>
									{narration}
								</p>
							)}
							{token && (
								<button
									onClick={() => {
										navigator.clipboard?.writeText(String(token));
										setCopied(true);
										setTimeout(() => setCopied(false), 1200);
									}}
									className='mt-1.5 inline-flex items-center gap-1 font-mono text-[12px] tabular-nums text-slate-800 hover:text-slate-950'
									title={isEmail ? 'Copy subject' : 'Copy reference'}>
									{isEmail ? 'Copy subject' : String(token)}
									<Copy size={11} className='text-slate-500' />
								</button>
							)}
							{copied && <span className='ml-1.5 text-[11px] text-emerald-700'>Copied</span>}
						</>
					)}
				</div>

				{/* Find */}
				<div className='min-w-0 rounded border border-slate-200 bg-slate-50/80 px-2.5 py-2'>
					<div className='text-[11px] font-semibold text-slate-700'>Find</div>
					{src && src.kind !== 'none' && src.search_hint ? (
						<p className='mt-1 text-[12px] leading-snug text-slate-700'>{src.search_hint}</p>
					) : (
						<p className='mt-1 text-[12px] text-slate-600'>No statement file hint.</p>
					)}
				</div>

				{/* Auth */}
				<div className='min-w-0 rounded border border-slate-200 bg-slate-50/80 px-2.5 py-2'>
					<div className='text-[11px] font-semibold text-slate-700'>Auth</div>
					<div className='mt-1 flex flex-wrap items-center gap-1.5'>
						{p.authorised_by_otp ? (
							<Tag tone='emerald'>OTP found</Tag>
						) : hasEmails ? (
							<Tag tone='amber'>No OTP</Tag>
						) : (
							<span className='text-[12px] text-slate-600'>No approval email in ±7 days</span>
						)}
						{hasEmails && (
							<span className='text-[12px] text-slate-600'>
								{p.email_evidence.length} email{p.email_evidence.length === 1 ? '' : 's'}
							</span>
						)}
					</div>
					{hasEmails && (
						<details className='group mt-1.5'>
							<summary className='cursor-pointer list-none text-[12px] font-medium text-slate-700 hover:text-slate-900'>
								<span className='inline-flex items-center gap-1'>
									<ChevronRight
										size={12}
										className='shrink-0 transition-transform group-open:rotate-90'
									/>
									Show emails
								</span>
							</summary>
							<div className='mt-1.5'>
								<EmailEvidenceList p={p} />
							</div>
						</details>
					)}
					{Array.isArray(p.email_evidence) && !p.authorised_by_otp && !hasEmails && (
						<p className='mt-1 text-[12px] text-amber-800'>
							Cannot say who authorised this payment.
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

/** Shared expand body — switches on PAYMENT_EXPAND_LAYOUT. */
const PaymentExpandBody: React.FC<{
	p: VendorPayment;
	detail: VendorDetail;
}> = ({ p, detail }) => {
	const invoiceBlock =
		(p.applied_bills?.length ?? 0) > 0 ? (
			<AppliedInvoices p={p} dateScoped={detail.dateScoped} window={detail.window} />
		) : (
			<div className='rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
				No invoice recorded in Zoho for this payment.
			</div>
		);

	const docs = (p.proofs ?? []).filter(pr => pr.proof_type !== 'note');
	const emailTrail =
		(p.email_refs?.length ?? 0) > 0 ? (
			<details className='group'>
				<summary className='flex cursor-pointer list-none items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900'>
					<ChevronRight
						size={12}
						className='shrink-0 transition-transform group-open:rotate-90'
					/>
					Email trail
					<span className='font-normal text-slate-600'>
						{p.email_refs.length} thread{p.email_refs.length === 1 ? '' : 's'}
					</span>
				</summary>
				<div className='mt-1.5 space-y-1.5'>
					{(p.email_refs ?? []).map(ref2 => (
						<div
							key={ref2.thread_id}
							className='rounded border border-slate-200 bg-white px-3 py-2 text-xs'>
							<div className='flex items-start gap-2'>
								<Mail size={12} className='mt-0.5 shrink-0 text-slate-500' />
								<div className='min-w-0'>
									<div className='font-medium text-slate-800'>{ref2.subject}</div>
									{ref2.summary && (
										<div className='mt-0.5 text-slate-600'>{ref2.summary}</div>
									)}
									<div className='mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600'>
										<span>{String(ref2.account || '').split('@')[0]}</span>
										<span>·</span>
										<span>
											matched on{' '}
											{ref2.matched_on === 'bill_number'
												? ref2.bills.join(', ')
												: 'bank reference'}
										</span>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</details>
		) : null;

	const docLinks =
		docs.length > 0 ? (
			<div className='flex flex-wrap gap-1.5'>
				{docs.map(pr => (
					<a
						key={pr.id}
						href={pr.view_url || undefined}
						target='_blank'
						rel='noreferrer'
						title={pr.label || pr.filename || ''}
						className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[12px] ${
							pr.view_url
								? 'border-slate-300 bg-white text-slate-800 hover:border-slate-400'
								: 'cursor-default border-slate-200 bg-slate-50 text-slate-500'
						}`}>
						<FileText size={11} />
						{pr.filename || pr.proof_type}
					</a>
				))}
			</div>
		) : null;

	if (PAYMENT_EXPAND_LAYOUT === 'v1') {
		return (
			<div className='space-y-4 border-t border-slate-100 bg-slate-50/50 px-3 py-4 pl-[34px]'>
				{p.reason && (
					<div className='rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800'>
						{p.reason}
					</div>
				)}
				{invoiceBlock}
				<VerificationStripV1 p={p} />
				{(p.email_evidence?.length ?? 0) > 0 && (
					<details className='group'>
						<summary className='flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700'>
							<ChevronRight
								size={11}
								className='shrink-0 transition-transform group-open:rotate-90'
							/>
							Authorisation &amp; correspondence
							<span className='font-normal normal-case tracking-normal text-slate-400'>
								{p.authorised_by_otp ? 'OTP found' : 'no OTP'} · {p.email_evidence.length} email
								{p.email_evidence.length === 1 ? '' : 's'}
							</span>
						</summary>
						<div className='mt-1.5'>
							<EmailEvidenceList p={p} />
						</div>
					</details>
				)}
				{p.email_evidence && !p.authorised_by_otp && (
					<div className='rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
						No approval email found for this amount within 7 days. We cannot say who authorised
						this payment.
					</div>
				)}
				{emailTrail}
				{docLinks}
			</div>
		);
	}

	// v2 — row already shows # / date / amount / status; expand is invoices + proof only
	const statusLabel =
		(p.reason_code && REASON_LABEL[p.reason_code]) || STATE_LABEL[p.state] || p.state;
	return (
		<div className='space-y-3 border-t border-slate-200 bg-white px-3 py-3 pl-4'>
			{(p.reason || p.reason_code) && (
				<div
					className={`rounded border px-3 py-2 text-xs ${
						p.reason
							? 'border-rose-200 bg-rose-50 text-rose-900'
							: 'border-amber-200 bg-amber-50 text-amber-900'
					}`}>
					<div className='font-semibold'>{statusLabel}</div>
					{p.reason && <p className='mt-0.5 leading-relaxed'>{p.reason}</p>}
				</div>
			)}

			{invoiceBlock}
			<PaymentProofTrayV2 p={p} />
			{emailTrail}
			{docLinks}
		</div>
	);
};

const VendorDrawer: React.FC<{
	detail: VendorDetail | null;
	loading: boolean;
	onClose: () => void;
}> = ({ detail, loading, onClose }) => {
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	useEffect(() => {
		setExpanded(new Set());
	}, [detail?.vendor.vendor_norm]);
	// v2 = accordion (one open); v1 = multi-open (legacy)
	const toggle = (paymentId: string) =>
		setExpanded(prev => {
			if (PAYMENT_EXPAND_LAYOUT === 'v2') {
				return prev.has(paymentId) ? new Set() : new Set([paymentId]);
			}
			const next = new Set(prev);
			if (next.has(paymentId)) next.delete(paymentId);
			else next.add(paymentId);
			return next;
		});

	const [tab, setTab] = useState<'payments' | 'invoices' | 'attention'>('payments');
	useEffect(() => {
		setTab('payments');
	}, [detail?.vendor.vendor_norm]);
	const ledger = detail ? buildLedger(detail) : null;
	const openOwed = ledger
		? ledger.openBills.reduce((a, b) => a + Number(b.balance || 0), 0)
		: 0;

	return (
		<div className='fixed inset-0 z-50 flex justify-end'>
			<div className='absolute inset-0 bg-black/30' onClick={onClose} />
			<div className='relative h-full w-full max-w-5xl overflow-y-auto bg-white shadow-2xl'>
				<div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10'>
					<h3 className='font-semibold text-gray-900'>
						{loading ? 'Loading…' : detail?.vendor.vendor_raw}
					</h3>
					<div className='flex items-center gap-3'>
						{detail && (
							<button
								onClick={() => {
									const scope = detail.window?.from
										? `${detail.window.from}_to_${detail.window.to}`
										: 'all';
									const name = detail.vendor.vendor_raw.replace(/[^a-zA-Z0-9]+/g, '-').slice(0, 40);
									if (tab === 'invoices') {
										downloadCsv(`${name}_invoices_${scope}.csv`, invoiceRows(detail));
									} else if (tab === 'attention' && ledger) {
										downloadCsv(
											`${name}_needs_attention_${scope}.csv`,
											ledger.openBills.map(b => ({
												vendor: detail.vendor.vendor_raw,
												invoice: b.bill_number,
												invoice_date: b.date,
												invoice_total: b.total,
												still_open: b.balance,
												why:
													isDust(b.balance)
														? 'dust'
														: Number(b.balance) > 0
														? 'still_owed'
														: 'nothing_settled_it',
											}))
										);
									} else {
										downloadCsv(`${name}_payments_${scope}.csv`, paymentRows(detail));
									}
								}}
								title='Download what is on screen — the Payments sheet is one row per payment-invoice pair'
								className='inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:border-slate-400'>
								<Download size={13} className='text-slate-400' />
								Export{' '}
								{tab === 'invoices'
									? 'invoices'
									: tab === 'attention'
									? 'needs attention'
									: 'payments'}
							</button>
						)}
						{detail && (
							<ReviewControl
								vendorNorm={detail.vendor.vendor_norm}
								initial={detail.vendor.review_status}
							/>
						)}
						<button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
							<X size={20} />
						</button>
					</div>
				</div>

				{loading || !detail ? (
					<div className='p-10 text-center text-gray-400'>
						<Loader2 className='animate-spin inline' size={20} />
					</div>
				) : (
					<>
						<div className='px-6 pt-6'>
							<div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
								<Metric label='Billed' value={inr(detail.totals.billed)} />
								<Metric label='Paid' value={inr(detail.totals.paid)} />
								<Metric label='Open' value={inr(detail.totals.open)} tone={detail.totals.open > 0 ? 'red' : 'green'} />
								<Metric
									label='Bank-verified'
									value={`${detail.totals.bank.proven}/${
										detail.totals.bank.proven + detail.totals.bank.unverified_name + detail.totals.bank.no_bank
									}`}
									sub={detail.totals.bank.unproven_value > 0 ? `${inr(detail.totals.bank.unproven_value)} unproven` : undefined}
									tone={detail.totals.bank.unproven_value > 0 ? 'red' : 'green'}
								/>
							</div>
							{detail.account && (
								<div className='mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3'>
									<div className='mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500'>
										Account with this vendor
										<span className='ml-1.5 font-normal normal-case tracking-normal text-slate-400'>
											all time, not limited to the selected period
										</span>
									</div>
									<div className='flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm'>
										<span className='text-slate-600'>
											They billed{' '}
											<span className='font-mono tabular-nums text-slate-900'>
												{inrFull(detail.account.billed)}
											</span>
											<span className='ml-1 text-xs text-slate-400'>
												({detail.account.bills} invoices)
											</span>
										</span>
										<span className='text-slate-600'>
											We paid{' '}
											<span className='font-mono tabular-nums text-slate-900'>
												{inrFull(detail.account.paid)}
											</span>
											<span className='ml-1 text-xs text-slate-400'>
												({detail.account.payments} payments)
											</span>
										</span>
										<span className='text-slate-600'>
											We still owe{' '}
											<span
												className={`font-mono text-base font-semibold tabular-nums ${
													detail.account.owed > DUST_MAX ? 'text-rose-700' : 'text-emerald-700'
												}`}>
												{inrFull(detail.account.owed)}
											</span>
											{detail.account.open_bills > 0 && (
												<span className='ml-1 text-xs text-slate-400'>
													({detail.account.open_bills} invoice
													{detail.account.open_bills === 1 ? '' : 's'}
													{detail.account.oldest_open
														? `, oldest ${shortDate(detail.account.oldest_open)}`
														: ''}
													)
												</span>
											)}
										</span>
										{detail.account.paid_not_yet_on_an_invoice > 1 && (
											<Tag tone='amber'>
												{inrFull(detail.account.paid_not_yet_on_an_invoice)} paid in advance
											</Tag>
										)}
										{detail.account.owed > 0 && detail.account.owed <= DUST_MAX && (
											<Tag tone='slate'>dust — rounding or TDS residual</Tag>
										)}
									</div>
								</div>
							)}

							<div className='text-xs text-gray-400 mt-2'>
								{detail.dateScoped && detail.window
									? `Scoped to ${detail.window.from} → ${detail.window.to}, same as the list`
									: 'All-time totals — no period selected in the list'}
							</div>
							{detail.totals.note && (
								<div className='mt-3 p-3 rounded bg-amber-50 text-amber-800 text-sm border border-amber-200'>
									{detail.totals.note}
								</div>
							)}
						</div>

					<div className='px-6 pb-8'>
						<div className='mb-3 flex items-center justify-between border-b border-slate-200'>
							<div className='flex gap-1' role='tablist'>
								{(
									[
										['payments', `Payments (${detail.payments.length})`, false],
										['invoices', `Invoices (${detail.bills.length})`, false],
										[
											'attention',
											`Needs attention (${ledger!.openBills.length})`,
											ledger!.openBills.length > 0,
										],
									] as const
								).map(([id, label, hot]) => (
									<button
										key={id}
										role='tab'
										aria-selected={tab === id}
										onClick={() => setTab(id)}
										className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
											tab === id
												? hot
													? 'border-rose-600 text-rose-800'
													: 'border-emerald-600 text-slate-900'
												: hot
												? 'border-transparent text-rose-700 hover:text-rose-800'
												: 'border-transparent text-slate-500 hover:text-slate-700'
										}`}>
										{label}
									</button>
								))}
							</div>
							<span className='text-xs text-slate-500'>
								{tab === 'payments'
									? 'one row per bank debit'
									: tab === 'attention'
									? 'still owed, or nothing on record settled it'
									: 'one row per invoice, with the payments that settled it'}
							</span>
						</div>
						{tab === 'invoices' && <InvoiceTable rows={ledger!.invoices} />}

						{tab === 'attention' && (
							<div>
								<div className='mb-3 flex items-baseline justify-between'>
									<p className='text-sm text-slate-700'>
										Money still owed, or nothing on record settled it.
									</p>
									<span className='text-xs text-slate-600'>
										{ledger!.openBills.length} of {detail.bills.length} invoice
										{detail.bills.length === 1 ? '' : 's'}
										{detail.dateScoped ? ' in this period' : ''}
										{' · '}
										<span className='font-mono tabular-nums font-semibold text-rose-700'>
											{inrFull(openOwed)}
										</span>{' '}
										owed
									</span>
								</div>
								{ledger!.openBills.length === 0 ? (
									<Empty text='Nothing needs attention in this period.' />
								) : (
									<div className='overflow-hidden rounded-lg border border-slate-200'>
										<div className='grid grid-cols-[1fr_92px_136px_1fr] items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500'>
											<span>Invoice</span>
											<span>Dated</span>
											<span className='text-right'>Invoice total</span>
											<span>Status</span>
										</div>
										{ledger!.openBills.map(b => (
											<div
												key={b.zoho_bill_id}
												className='grid grid-cols-[1fr_92px_136px_1fr] items-center gap-3 border-b border-slate-100 px-3 py-2.5 text-xs last:border-0'>
												<span className='truncate font-medium text-slate-900'>{b.bill_number}</span>
												<span className='font-mono tabular-nums text-slate-600'>
													{shortDate(b.date)}
												</span>
												<span className='text-right font-mono tabular-nums text-slate-800'>
													{inrFull(b.total)}
												</span>
												<span className='flex flex-wrap gap-1.5'>
													{isDust(b.balance) ? (
														<Tag tone='slate'>
															{inrFull(b.balance)} left — dust, rounding or TDS
														</Tag>
													) : Number(b.balance) > 0 ? (
														<Tag tone='rose'>{inrFull(b.balance)} still owed</Tag>
													) : (
														<Tag tone='amber'>nothing on record settled it</Tag>
													)}
												</span>
											</div>
										))}
									</div>
								)}
							</div>
						)}

						{tab === 'payments' && (
						<>
						<div className='mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500'>
							<span className='flex items-center gap-1.5'>
								<span className='font-semibold uppercase tracking-[0.1em] text-slate-400'>Proof</span>
								<LegendDot on label='bank' />
								<LegendDot on label='invoice' />
								<LegendDot on label='email' />
								<LegendDot on label='document' />
								<span className='text-slate-400'>— filled means present</span>
							</span>
							<span className='flex items-center gap-1'>
								<span className='font-semibold uppercase tracking-[0.1em] text-slate-400'>Invoices</span>
								<span className='text-amber-600'>*</span>
								<span className='text-slate-400'>= some are dated outside the selected period</span>
							</span>
						</div>

						{ledger!.months.length === 0 ? (
							<Empty text='No payments recorded in this period.' />
						) : (
							<div className='overflow-hidden rounded-lg border border-slate-200'>
								<div className='grid grid-cols-[16px_92px_1fr_136px_88px_1fr_64px_112px] items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500'>
									<span />
									<span>Date</span>
									<span>Payment</span>
									<span className='text-right'>Amount</span>
									<span className='text-right'>Invoices</span>
									<span title='Bank reference where one exists, otherwise the Zoho payment number'>
										Reference
									</span>
									<span className='text-center' title='Bank · Invoice · Email · Document'>Proof</span>
									<span>Status</span>
								</div>

								{ledger!.months.map(month => (
									<div key={month.key}>
										<div className='flex items-baseline justify-between border-b border-slate-100 bg-slate-50/60 px-3 py-1.5'>
											<span className='text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500'>
												{month.label}
											</span>
											<span className='font-mono text-[11px] tabular-nums text-slate-500'>
												{inrFull(month.total)}
											</span>
										</div>

										{month.payments.map((p, pi) => {
											const sameDay =
												month.payments.filter(x => x.payment_date === p.payment_date).length > 1;
											const isOpen = expanded.has(p.zoho_payment_id);
											// always show an identifier; greyed when it is only a Zoho number
											const ref = p.reference?.value ?? null;
											const refWeak = !p.reference || p.reference.kind === 'zoho_only';
											const v2Card =
												PAYMENT_EXPAND_LAYOUT === 'v2'
													? `overflow-hidden rounded-md border-l-4 transition-[margin,box-shadow,background-color,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
															isOpen
																? `my-1.5 border border-slate-200 bg-white shadow-sm ${paymentAccentBorder(p)}`
																: 'my-0 border border-transparent border-b-slate-100 shadow-none border-l-transparent'
													  }`
													: 'border-b border-slate-100 last:border-0';
											return (
												<div key={p.zoho_payment_id} className={v2Card}>
													<button
														onClick={() => toggle(p.zoho_payment_id)}
														aria-expanded={isOpen}
														className={`grid w-full grid-cols-[16px_92px_1fr_136px_88px_1fr_64px_112px] items-center gap-3 px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500 ${
															isOpen && PAYMENT_EXPAND_LAYOUT === 'v2'
																? 'bg-slate-50/90'
																: 'hover:bg-slate-50/80 focus:bg-slate-50'
														}`}>
														<span className='flex items-center'>
															<span className={`mr-1.5 h-7 w-[3px] rounded-full ${paymentDot(p)}`} />
															<ChevronRight
																size={13}
																className={`shrink-0 text-slate-500 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
																	isOpen ? 'rotate-90' : ''
																}`}
															/>
														</span>
														<span className='font-mono text-xs tabular-nums text-slate-700'>
															{shortDate(p.payment_date)}
															{sameDay && (
																<span
																	className='ml-1 text-slate-500'
																	title='More than one payment on this date. Zoho records no time, so these are ordered by payment number — the same order the running balances use.'>
																	·{pi + 1}
																</span>
															)}
														</span>
														<span className='truncate text-sm text-slate-900'>
															#{p.payment_number}
															{p.paid_through_account_name && (
																<span className='ml-1.5 text-xs text-slate-600'>
																	{p.paid_through_account_name}
																</span>
															)}
														</span>
														<span className='text-right font-mono text-sm font-semibold tabular-nums text-slate-900'>
															{inrFull(p.amount)}
														</span>
														<span className='text-right font-mono text-xs tabular-nums text-slate-700'>
															{p.covers_bills || '—'}
															{p.bills_outside_period > 0 && (
																<span className='ml-0.5 text-amber-700' title={`${p.bills_outside_period} dated outside this period`}>
																	*
																</span>
															)}
														</span>
														<span
															className={`truncate font-mono text-[11px] tabular-nums ${
																refWeak ? 'italic text-slate-500' : 'text-slate-700'
															}`}
															title={p.reference ? `${p.reference.value} — ${p.reference.label}` : undefined}>
															{ref || '—'}
														</span>
														<span className='flex justify-center'>
															<ProofDots p={p} />
														</span>
														<span
															className={`text-xs font-medium leading-snug ${
																(p.reason_code && REASON_TONE[p.reason_code]) ||
																STATE_TEXT[p.state] ||
																'text-slate-600'
															}`}
															title={
																[
																	(p.reason_code && REASON_LABEL[p.reason_code]) ||
																		STATE_LABEL[p.state] ||
																		p.state,
																	p.reason,
																]
																	.filter(Boolean)
																	.join(' — ')
															}>
															{(p.reason_code && REASON_LABEL[p.reason_code]) ||
																STATE_LABEL[p.state] ||
																p.state}
														</span>
													</button>

													{detail && (
														<SmoothCollapse open={isOpen}>
															<PaymentExpandBody p={p} detail={detail} />
														</SmoothCollapse>
													)}
												</div>
											);
										})}
									</div>
								))}
							</div>
						)}

						</>
						)}

						{ledger!.unmatchedThreads.length > 0 && (
							<details className='mt-6'>
								<summary className='cursor-pointer text-sm font-semibold text-slate-800'>
									Other emails about this vendor ({ledger!.unmatchedThreads.length})
								</summary>
								<div className='mt-1 text-xs text-slate-500'>
									These name no invoice number, so they cannot be attached to a specific payment.
								</div>
								<div className='mt-2 space-y-2'>
									{ledger!.unmatchedThreads.slice(0, 20).map(t => (
										<div key={t.provider_thread_id} className='rounded-lg border border-slate-200 p-3'>
											<div className='text-sm font-medium text-slate-800'>{t.subject}</div>
											<div className='mt-1 text-xs text-slate-500'>{t.one_line_summary}</div>
											<div className='mt-2 flex flex-wrap items-center gap-2 text-xs'>
												<span className='text-slate-400'>{t.account_email.split('@')[0]}</span>
												{t.counterparty_name && (
													<span className='text-slate-400'>· about {t.counterparty_name}</span>
												)}
												{t.discrepancy && t.discrepancy !== 'consistent' && (
													<Tag tone='amber'>{t.discrepancy}</Tag>
												)}
												{t.claimed_paid ? <Tag tone='slate'>claims paid</Tag> : null}
												{t.no_reply_computed ? <Tag tone='rose'>no reply</Tag> : null}
											</div>
										</div>
									))}
								</div>
							</details>
						)}
					</div>
					</>
				)}
			</div>
		</div>
	);
};

const Metric: React.FC<{ label: string; value: string; sub?: string; tone?: 'default' | 'green' | 'red' }> = ({
	label,
	value,
	sub,
	tone = 'default',
}) => (
	<div className='p-3 rounded-lg bg-gray-50 border border-gray-100'>
		<div className='text-xs text-gray-500'>{label}</div>
		<div
			className={`text-lg font-bold ${
				tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-600' : 'text-gray-900'
			}`}>
			{value}
		</div>
		{sub && <div className='text-xs text-gray-400 mt-0.5'>{sub}</div>}
	</div>
);

/** One small status word. Tone carries meaning, never decoration. */
const Tag: React.FC<{ tone: 'rose' | 'amber' | 'slate' | 'emerald'; children: React.ReactNode }> = ({ tone, children }) => {
	const cls = {
		rose: 'bg-rose-50 text-rose-700 ring-rose-200',
		amber: 'bg-amber-50 text-amber-700 ring-amber-200',
		slate: 'bg-slate-100 text-slate-600 ring-slate-200',
		emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
	}[tone];
	return (
		<span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>
			{children}
		</span>
	);
};

const Empty: React.FC<{ text: string }> = ({ text }) => (
	<div className='px-3 py-4 text-sm text-gray-400 text-center'>{text}</div>
);

/** Manual review flag a human sets on a vendor: needs review / reviewed ✓ / clear. */
const ReviewControl: React.FC<{
	vendorNorm: string;
	initial: 'needs_review' | 'reviewed_ok' | null;
}> = ({ vendorNorm, initial }) => {
	const [status, setStatus] = useState<'needs_review' | 'reviewed_ok' | null>(initial);
	const [saving, setSaving] = useState(false);
	useEffect(() => setStatus(initial), [vendorNorm, initial]);

	const set = async (s: 'needs_review' | 'reviewed_ok' | 'clear') => {
		setSaving(true);
		try {
			const r = await setVendorReview(vendorNorm, s);
			setStatus(r.review_status);
		} finally {
			setSaving(false);
		}
	};

	const btn = (active: boolean, tone: 'blue' | 'green') =>
		`px-2 py-1 rounded text-xs font-medium border transition ${
			active
				? tone === 'green'
					? 'bg-green-600 text-white border-green-600'
					: 'bg-blue-600 text-white border-blue-600'
				: 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
		}`;

	return (
		<div className={`flex items-center gap-1 ${saving ? 'opacity-50 pointer-events-none' : ''}`}>
			<button onClick={() => set('needs_review')} className={btn(status === 'needs_review', 'blue')}>
				Needs review
			</button>
			<button onClick={() => set('reviewed_ok')} className={btn(status === 'reviewed_ok', 'green')}>
				Reviewed ✓
			</button>
			{status && (
				<button
					onClick={() => set('clear')}
					className='px-1.5 py-1 rounded text-xs text-gray-400 hover:text-gray-600'
					title='Clear review flag'>
					✕
				</button>
			)}
		</div>
	);
};
